import {Observable} from 'rxjs';
import {CpmCApiBridge} from '../../../../../src/app/cpmc-api-bridge';
import {AllowedQueryParamTypes, QueryParams} from '../../../../../sdk/src/utils/query.utils';
import {CpmCGlobalFileEntryApiModel} from '../../models/c-global/cpmc-global-file-entry.api-model';
import {CpmCCustomerFileEntryApiModel} from '../../models/c-customer/cpmc-customer-file-entry.api-model';
import {
    CpmCFileAddUserFileApiSchema,
    CpmCFileCreateFileEntryForUploadApiSchema,
    CpmCFileCreateUserDirApiSchema,
    CpmCFileUpdateUserFileApiSchema
} from '../../schemas/c-common/cpmc-file.api-schemas';
import {CpmCPaginatedResultAPISchema} from '../../schemas/cpmc-paginated-result.api-schemas';

export type CpmCFileEntryApiModel = CpmCGlobalFileEntryApiModel | CpmCCustomerFileEntryApiModel;
export type CpmCGetUserFileListParams = {
    [K in keyof CpmCFileEntryApiModel]?: CpmCFileEntryApiModel[K] extends AllowedQueryParamTypes
        ? CpmCFileEntryApiModel[K]
        : never;
} & {parent_uuid?: string | null; parent_path?: string} & QueryParams;

/**
 * File Api Service
 */
export class CpmCFileApiService {
    public static baseUrl(inCustomerUUID: string | undefined) {
        const base = CpmCApiBridge.getBackendBaseURL() + 'api/core/v1/';
        const customerUUID = CpmCApiBridge.getCurrentCustomerUUID();

        if (inCustomerUUID !== undefined || (inCustomerUUID === undefined && customerUUID)) {
            return base + `customer/${inCustomerUUID || customerUUID}/file`;
        } else {
            return base + `file`;
        }
    }

    // ===============================================================
    // ===============================================================

    public static endpointListUserFiles(inCustomerUUID: string | undefined): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/list-user-files`;
    }
    public static endpointGet(inCustomerUUID: string | undefined, fileEntryUUID: string): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/${fileEntryUUID}`;
    }
    public static endpointResolvedUserFilePath(inCustomerUUID: string | undefined, fileEntryUUID: string): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/${fileEntryUUID}/resolved-user-file-path`;
    }
    public static endpointDownload(
        inCustomerUUID: string | undefined,
        fileEntryUUID: string,
        redirect: boolean
    ): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/${fileEntryUUID}/download${redirect ? '?redirect=true' : ''}`;
    }
    public static endpointCreateFileEntryForUpload(inCustomerUUID: string | undefined): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}`;
    }
    public static endpointAddUserFile(inCustomerUUID: string | undefined): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/user-file`;
    }
    public static endpointCreateUserDir(inCustomerUUID: string | undefined): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/user-dir`;
    }
    public static endpointUpdateUserFile(inCustomerUUID: string | undefined, fileEntryUUID: string): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/${fileEntryUUID}/user-file`;
    }
    public static endpointDeleteUserFile(inCustomerUUID: string | undefined, fileEntryUUID: string): string {
        return `${CpmCFileApiService.baseUrl(inCustomerUUID)}/${fileEntryUUID}/user-file`;
    }

    // ===============================================================
    // ===============================================================

    public static getUserFileList(
        params: CpmCGetUserFileListParams
    ): Observable<CpmCPaginatedResultAPISchema<CpmCFileEntryApiModel>> {
        return CpmCApiBridge.httpGet<CpmCPaginatedResultAPISchema<CpmCFileEntryApiModel>>(
            CpmCFileApiService.endpointListUserFiles(undefined),
            {
                ...(params as QueryParams)
            }
        );
    }
    public static getFileEntryByUUID(fileEntryUUID: string): Observable<CpmCFileEntryApiModel> {
        return CpmCApiBridge.httpGet<CpmCFileEntryApiModel>(CpmCFileApiService.endpointGet(undefined, fileEntryUUID));
    }
    public static getResolvedUserFilePath(fileEntryUUID: string): Observable<string> {
        return CpmCApiBridge.httpGet<string>(CpmCFileApiService.endpointResolvedUserFilePath(undefined, fileEntryUUID));
    }
    public static getDownloadByUUID(fileEntryUUID: string): Observable<string> {
        return CpmCApiBridge.httpGet<string>(CpmCFileApiService.endpointDownload(undefined, fileEntryUUID, false));
    }
    public static postCreateFileEntryForUpload(
        payload: CpmCFileCreateFileEntryForUploadApiSchema
    ): Observable<{file_store_entry: CpmCFileEntryApiModel; upload_url: string}> {
        return CpmCApiBridge.httpPost<{file_store_entry: CpmCFileEntryApiModel; upload_url: string}>(
            CpmCFileApiService.endpointCreateFileEntryForUpload(undefined),
            payload
        );
    }
    public static postAddUserFile(payload: CpmCFileAddUserFileApiSchema): Observable<CpmCFileEntryApiModel> {
        return CpmCApiBridge.httpPost<CpmCFileEntryApiModel>(
            CpmCFileApiService.endpointAddUserFile(undefined),
            payload
        );
    }
    public static postCreateUserDir(payload: CpmCFileCreateUserDirApiSchema): Observable<CpmCFileEntryApiModel> {
        return CpmCApiBridge.httpPost<CpmCFileEntryApiModel>(
            CpmCFileApiService.endpointCreateUserDir(undefined),
            payload
        );
    }
    public static patchUpdateUserFile(
        fileEntryUUID: string,
        payload: CpmCFileUpdateUserFileApiSchema
    ): Observable<CpmCFileEntryApiModel> {
        return CpmCApiBridge.httpPatch<CpmCFileEntryApiModel>(
            CpmCFileApiService.endpointUpdateUserFile(undefined, fileEntryUUID),
            payload
        );
    }
    public static deleteUserFile(fileEntryUUID: string): Observable<boolean> {
        return CpmCApiBridge.httpDelete<boolean>(CpmCFileApiService.endpointDeleteUserFile(undefined, fileEntryUUID));
    }
}
